import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import { relativeTime } from "../../lib/time";
import { AttentionBadge } from "../sales/AttentionBadge";
import { toneClass } from "../sales/CallRow";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";

/**
 * Sales calls, on the account they are about.
 *
 * A call is raisable from two places on this page -- the toolbar at the top and
 * the button here -- and both open the one dialog the page owns, pre-linked to
 * this account. Somebody who has decided an account needs a ring should never
 * have to change product and find the company again by name.
 *
 * The list is every call ever raised, not the latest one. Going back to a
 * company months later is a *new* call, and the previous pursuit's attempts and
 * score stay exactly as they were -- so the honest rendering is a history, with
 * the open one at the top because it is the only one anybody can act on.
 *
 * Closed calls also write themselves into the timeline to the left, with their
 * score, notes and every attempt. This panel is the index; that is the record.
 */
export function SalesCallsPanel({
  companyId,
  onRaise,
}: {
  companyId: number;
  /** Opens the dialog the page owns; the toolbar button opens the same one. */
  onRaise: () => void;
}) {
  const calls = useQuery({
    queryKey: queryKeys.salesForCompany(companyId),
    queryFn: () => salesApi.forCompany(companyId),
    staleTime: 60_000,
  });

  const open = (calls.data ?? []).filter((call) => call.status === "open");

  return (
    <>
      <section className="rounded-lg border border-line bg-surface">
        <header className="flex items-center gap-2 border-b border-line px-3 py-2">
          <h2 className="text-sm font-medium text-content">Sales calls</h2>
          {open.length > 0 ? (
            <span className="rounded-full bg-surface-2 px-1.5 text-[10px] text-faint">
              {open.length} open
            </span>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            onClick={onRaise}
          >
            Raise a call
          </Button>
        </header>

        {calls.isLoading ? (
          <p className="p-3 text-center text-xs text-faint">Loading…</p>
        ) : (calls.data?.length ?? 0) === 0 ? (
          <p className="p-3 text-center text-xs text-faint">
            Nobody has raised a sales call for this account.
          </p>
        ) : (
          <ul>
            {calls.data!.map((call) => (
              <li key={call.id} className="border-b border-line last:border-b-0">
                <Link
                  to={`/sales/calls/${call.id}`}
                  className="flex flex-col gap-0.5 px-3 py-2 hover:bg-surface-2"
                >
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="min-w-0 truncate text-xs text-content">
                      {call.subject ?? "Sales call"}
                    </span>
                    <AttentionBadge attention={call.attention} size="xs" />
                    {call.status === "closed" ? (
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                          toneClass(call.scoreTone),
                        )}
                      >
                        {call.scoreLabel ?? "Closed"}
                      </span>
                    ) : null}
                  </span>

                  <span className="text-[11px] text-faint">
                    {call.status === "closed"
                      ? `closed ${call.closedAt ? relativeTime(call.closedAt) : ""} by ${call.closedByName ?? "somebody"}`
                      : call.assigneeName
                        ? `with ${call.assigneeName}`
                        : "in the pool"}
                    {" · "}
                    {call.attemptCount === 0
                      ? "no attempts"
                      : `${call.attemptCount} ${call.attemptCount === 1 ? "attempt" : "attempts"}`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
